import { ReactNode, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { AnimatePresence, motion } from 'framer-motion';

import {
  Box,
  BoxProps,
  Flex,
  Heading,
  List,
  ListIcon,
  ListItem,
  Portal,
  Skeleton,
  Stack,
  Tag,
  Text,
} from '@chakra-ui/react';
import { CheckIcon } from '@udacity/chakra-uds-icons';
import { headerHeight } from '@udacity/udacity-header-v2';

import { Card } from '~/components/card';
import { CouponAppliedTag } from '~/components/coupon-applied-tag';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { trackEvent } from '~/features/analytics/track-event';
import { BillingFrequency, billingFrequencySchema } from '~/features/checkout/models/checkout';
import { useExperimentalSegmentEvents } from '~/features/experiments/ab_plp_redesign_2025/hooks';
import { useCurrency } from '~/features/internationalization/hooks/use-currency';
import { usePricePlans } from '~/features/plans/hooks/use-price-plans';
import { useStandaloneProgramPrice } from '~/features/plans/hooks/use-standalone-program-price';
import { Program } from '~/features/program/models/program';

import { CountdownCompactContainer } from './countdown-compact';
import { PricePlanRadioGroup } from './plan-radio';

export const STANDALONE_FEATURES = [
  '**Flexibility to finish the program in your own time**',
  'Access to the individual program',
  'Hands-on projects with expert feedback',
  'Personalized project reviews',
  'Program Certificate',
];

const ALL_ACCESS_FEATURES = [
  '**Cancel Anytime**',
  '**Unlimited access to hundreds of top-rated courses**',
  'Hands-on projects with expert feedback',
  'Personalized career coaching and interview prep',
  'Program Certificates',
];

export const LEARNING_PLAN_FEATURES = [
  'Complete the program in your own time',
  '20+ courses from industry experts',
  'Topics like Ethical AI, Blockchain, Quantum, and more',
  'Certificates with every course',
];

export type PriceCardProps = {
  learningPlanKey?: string;
  programKey: string;
  programTitle: string;
  programSemanticType: Program['semanticType'];
  renderButton?: (selectedPlan: BillingFrequency, buttonLabel: string) => ReactNode;
  isMobile?: boolean;
  defaultSelectedPlan?: BillingFrequency;
  leadImageUrl?: string | null;
};

export function PriceCard({
  programKey,
  programTitle,
  programSemanticType,
  renderButton,
  isMobile = false,
  learningPlanKey,
  defaultSelectedPlan,
  leadImageUrl,
  ...cardProps
}: PriceCardProps & BoxProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedPlan, setSelectedPlan] = useState<BillingFrequency>(
    defaultSelectedPlan || billingFrequencySchema.enum.monthly
  );
  const [showMobileBanner, setShowMobileBanner] = useState<boolean>(false);
  const { currency } = useCurrency();
  const { data: plans } = usePricePlans(currency);
  const { data: standalonePlan, isFetched: standalonePlanIsFetched } = useStandaloneProgramPrice({
    nodeKey: programKey,
    learningPlanKey,
  });
  const isExperimentalTrackEventsEnabled = useExperimentalSegmentEvents('dino')?.variant === 'on';

  useEffect(() => {
    const node = containerRef.current;
    if (!isMobile || !node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setShowMobileBanner(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        }
      },
      {
        threshold: 0,
      }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobile]);

  const monthlyPlan = plans?.prices[billingFrequencySchema.enum.monthly]?.individual;
  const fourMonthPlan = plans?.prices[billingFrequencySchema.enum['4month']]?.individual;

  const ALL_ACCESS_PLAN_ITEMS = [
    {
      label: 'Subscription · Monthly',
      value: billingFrequencySchema.enum.monthly,
      features: ALL_ACCESS_FEATURES,
      ...monthlyPlan,
      displayPrice: monthlyPlan?.displayPricePerMonth,
      displayPriceUnits: ' / month',
    },
    {
      label: 'Subscription · Bundle',
      value: billingFrequencySchema.enum['4month'],
      features: ALL_ACCESS_FEATURES,
      ...fourMonthPlan,
      displayPrice: fourMonthPlan?.displayPricePerMonth,
      displayPriceUnits: ' / month',
      strikeThroughDisplayPrice: fourMonthPlan?.strikeThroughDisplayPricePerMonth,
    },
  ];

  const STANDALONE_PLAN_ITEM = {
    label: 'Individual Course',
    value: billingFrequencySchema.enum.purchase,
    features: STANDALONE_FEATURES,
    ...standalonePlan,
    displayPriceUnits: ' / one-time payment',
  };

  const activePlan = (() => {
    if (selectedPlan === billingFrequencySchema.enum.purchase) {
      if (programKey) {
        return STANDALONE_PLAN_ITEM;
      }
    }

    return ALL_ACCESS_PLAN_ITEMS.find((item) => item.value === selectedPlan);
  })();

  const cardTitle = activePlan?.value === billingFrequencySchema.enum.purchase ? programTitle : activePlan?.label;

  const CARD_BORDER_RADIUS = '8px';

  function renderPrice({
    billingFrequency,
    displayPrice,
    displayPriceUnits,
    strikeThroughDisplayPrice,
    couponApplied,
    couponPercentOff,
    savingsBlurb,
  }: {
    billingFrequency: BillingFrequency;
    displayPrice: string | undefined;
    displayPriceUnits: string | undefined;
    strikeThroughDisplayPrice: string | null | undefined;
    couponApplied: boolean | undefined;
    couponPercentOff: number | null | undefined;
    savingsBlurb: string | null | undefined;
  }) {
    if (!displayPrice)
      return (
        <Flex alignItems='center' flexDirection='row' gap={2}>
          <Skeleton height='40px' width='120px' />
          <Skeleton height='27px' width='120px' />
        </Flex>
      );
    else if (strikeThroughDisplayPrice) {
      return (
        <>
          <Flex alignItems='center' gap={2}>
            <Heading alignItems='center' as='p' display='flex' flexWrap='wrap' gap='4px' size='h2'>
              <Heading
                as='del'
                color='gray.600'
                position='relative'
                size='marketing-2xl'
                textDecor='none'
                sx={{
                  '&::after': {
                    content: '""',
                    backgroundColor: 'gray.600',
                    width: '100%',
                    position: 'absolute',
                    height: '.1em',
                    borderRadius: '.1em',
                    left: '-5%',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    transform: 'translateY(-50%) rotate(-16deg)',
                    top: '50%',
                  },
                }}
              >
                {strikeThroughDisplayPrice}
              </Heading>
            </Heading>
            {couponApplied && typeof couponPercentOff === 'number' && <CouponAppliedTag value={couponPercentOff} />}
          </Flex>
          <Heading
            alignItems='center'
            as='ins'
            color='black'
            display='flex'
            flexWrap='wrap'
            fontWeight='500'
            gap={2}
            size='marketing-4xl'
            textDecor='none'
          >
            {displayPrice}{' '}
            <Heading as='span' size='marketing-lg'>
              {displayPriceUnits}
            </Heading>
          </Heading>
        </>
      );
    } else {
      return (
        <Flex flexWrap='wrap' gap={3}>
          <Heading
            alignItems='center'
            as='p'
            color='black'
            display='flex'
            fontWeight='500'
            gap={2}
            size='marketing-4xl'
          >
            {displayPrice}{' '}
            <Heading as='span' size='marketing-lg'>
              {displayPriceUnits}
            </Heading>
          </Heading>
          {billingFrequency === billingFrequencySchema.enum['4month'] && !couponApplied && savingsBlurb && (
            <Tag
              alignSelf='center'
              as='span'
              bg='blue.400'
              border='none'
              color='white'
              fontFamily='body'
              fontWeight='600'
              textTransform='capitalize'
              whiteSpace='nowrap'
              fontSize={{
                base: '2xs',
                lg: 'xs',
              }}
            >
              {savingsBlurb}
            </Tag>
          )}
        </Flex>
      );
    }
  }

  function getAvailablePlans() {
    if (programSemanticType === 'Course') return [];
    // Note: this is a way to prevent layout shifts when standalone plan is fetching.
    // Since Nanodegree programs _should_ have a standalone plan, we will optimistically show the standalone plan with a skeleton over the price.
    if (programKey && !standalonePlanIsFetched) return [...ALL_ACCESS_PLAN_ITEMS, STANDALONE_PLAN_ITEM];

    if (standalonePlanIsFetched && !standalonePlan) return ALL_ACCESS_PLAN_ITEMS;

    return [...ALL_ACCESS_PLAN_ITEMS, STANDALONE_PLAN_ITEM];
  }

  function handlePlanChange(plan: BillingFrequency) {
    if (isExperimentalTrackEventsEnabled) {
      let label = `${plan}_pricecard_clicked`;
      if (plan === billingFrequencySchema.enum.purchase) {
        label = 'onetime_purchase_pricecard_clicked';
      }
      trackEvent({
        name: 'Product Page CTA Clicked',
        type: 'click',
        label,
        additionalAttributes: {
          product_ids: [programKey],
        },
      });
    }
    setSelectedPlan(plan);
  }

  const availablePlans = getAvailablePlans();

  return (
    <>
      <Stack ref={containerRef} gap={0} {...cardProps} data-testid='price-card'>
        {leadImageUrl && (
          <Image
            alt=''
            data-testid='price-card-lead-image'
            height={200}
            quality={75}
            src={leadImageUrl}
            style={{ borderRadius: CARD_BORDER_RADIUS, pointerEvents: 'none', marginBottom: '-8px' }}
            width={400}
            priority
          />
        )}
        <Card
          backdropFilter='blur(8px)'
          borderColor='gray.300'
          borderRadius={CARD_BORDER_RADIUS}
          data-testid={isMobile ? 'mobile-plans' : 'desktop-plans'}
          display='flex'
          flexDirection='column'
          gap={8}
          padding='16px'
          scrollMarginTop={`calc(${headerHeight} + 24px)`}
        >
          <PricePlanRadioGroup items={availablePlans} value={selectedPlan} onChange={handlePlanChange} />
          <Flex flex='1' flexDirection='column' gap={2}>
            <Heading as='p' size='marketing-xl'>
              {cardTitle}
              {activePlan?.value === billingFrequencySchema.enum['4month'] && (
                <Box as='sup' fontSize='sm'>
                  1
                </Box>
              )}
            </Heading>

            {activePlan && (
              <>
                {renderPrice({
                  displayPrice: activePlan.displayPrice,
                  displayPriceUnits: activePlan.displayPriceUnits,
                  strikeThroughDisplayPrice: activePlan.strikeThroughDisplayPrice,
                  couponApplied: activePlan.couponApplied,
                  couponPercentOff: activePlan.couponPercentOff,
                  billingFrequency: activePlan.value,
                  savingsBlurb:
                    activePlan.value === billingFrequencySchema.enum['4month'] ? activePlan.savingsBlurb : undefined,
                })}
                <List
                  borderTop='1px solid'
                  borderTopColor='accent.sky.100'
                  display='flex'
                  flexDirection='column'
                  gap={1}
                  py={4}
                >
                  {activePlan.features.map((feature) => (
                    <ListItem key={feature} display='flex'>
                      <ListIcon alignSelf='flex-start' as={CheckIcon} color='blue.500' height='20px' width='20px' />
                      <HeadingWithEmphasis
                        text={feature}
                        emphasisProps={{
                          fontWeight: '600',
                        }}
                        headingProps={{
                          as: 'span',
                          size: 'xs',
                          fontFamily: 'body',
                          fontWeight: '400',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>

                <CountdownCompactContainer
                  renderButton={(plan, buttonLabel) => renderButton?.(plan, buttonLabel)}
                  selectedPlan={selectedPlan}
                />
              </>
            )}

            {activePlan?.value === billingFrequencySchema.enum['4month'] && (
              <List as='ol' color='gray.700' display='flex' flexDirection='column' gap={1}>
                <ListItem>
                  <Text fontSize='2xs'>
                    <Box as='sup'>1</Box>Discount applies to the first 4 months of membership, after which plans are
                    converted to month-to-month.
                  </Text>
                </ListItem>
                <ListItem>
                  <Text fontSize='2xs'>
                    <Box as='sup'>2</Box>Total price of {fourMonthPlan?.displayPrice} for a 4-month bundle, based on the
                    average time to complete a Nanodegree program.
                  </Text>
                </ListItem>
              </List>
            )}
          </Flex>
        </Card>
      </Stack>
      {isMobile && (
        <Portal>
          <AnimatePresence>
            {showMobileBanner && (
              <Box
                key='sticky-program-banner'
                as={motion.div}
                bg='white'
                borderTop='solid 1px'
                borderTopColor='gray.300'
                bottom='0'
                data-testid='sticky-program-banner'
                p='16px'
                position='fixed'
                shadow='lg'
                width='100%'
                animate={{
                  transition: { type: 'linear' },
                  opacity: 1,
                  y: 0,
                }}
                display={{
                  base: 'block',
                  md: 'none',
                }}
                exit={{
                  opacity: 0,
                  y: 100,
                }}
                initial={{
                  opacity: 0,
                  y: 100,
                }}
              >
                {renderButton?.(selectedPlan, 'Enroll Now')}
              </Box>
            )}
          </AnimatePresence>
        </Portal>
      )}
    </>
  );
}
